import React, { useCallback, useMemo, useRef } from 'react';
import { intersection } from 'lodash';

import { useTranslation } from '@lyearn/i18n';
import { TagTypeEnum } from '@/types/schema';

import { ControlTagValues, SelectedTagValues, TagsMenuItem, TagValues } from '../../types';
import { ControlledByMap, SourceToTargetMap, TagsIdToNameMap } from '../AttachedTags/types';
import DateSelect from './DateSelect';
import MultiSelect from './MultiSelect';
import NumberSelect from './NumberSelect';
import SingleSelect from './SingleSelect';
import TextSelect from './TextSelect';
import { TagPaginatedFilters } from './types';
import UserSelect from './UserSelect';

const TAG_VALUE_SELECT = {
  [TagTypeEnum.Date]: DateSelect,
  [TagTypeEnum.MultiSelect]: MultiSelect,
  [TagTypeEnum.SingleSelect]: SingleSelect,
  [TagTypeEnum.Number]: NumberSelect,
  [TagTypeEnum.Text]: TextSelect,
  [TagTypeEnum.User]: UserSelect,
};

function TagValueSelector({
  tag,
  className,
  selectedTagValues,
  onDeleteTag,
  onTagsValueChange,
  error,
  children,
  readOnly,
  isSharedItem,
  filters,
  controlledByMap,
  sourceToTargetMap,
  getFilteredSelectedValues,
  tagsIdToNameMap = {},
}: {
  tag: TagsMenuItem;
  className?: string;
  selectedTagValues?: SelectedTagValues;
  onDeleteTag: (tagId: string) => void;
  error?: string;
  onTagsValueChange: (tags: SelectedTagValues) => void;
  readOnly: boolean;
  children?: React.ReactNode;
  isSharedItem?: boolean;
  filters?: TagPaginatedFilters;
  controlledByMap?: ControlledByMap;
  sourceToTargetMap?: SourceToTargetMap;
  getFilteredSelectedValues?: (tagId: string, val: TagValues) => SelectedTagValues;
  tagsIdToNameMap?: TagsIdToNameMap;
}) {
  const TagValueSelectorComponent = TAG_VALUE_SELECT[tag.type];
  const selectedTagValue = selectedTagValues?.[tag.id];

  const isTagControlled = Boolean(controlledByMap?.[tag.id]);
  const controllingTagIds = controlledByMap?.[tag.id];

  const { t } = useTranslation();

  const onChange = useCallback(
    (val: TagValues) => {
      if (![TagTypeEnum.MultiSelect, TagTypeEnum.SingleSelect].includes(tag.type) || !val) {
        onTagsValueChange({
          ...selectedTagValues,
          [tag.id]: val,
        });
      } else {
        // const tagIdsAffected = getAllControlledTagIds?.(tag.id);

        // onTagsValueChange({
        //   ...omit(selectedTagValues, tagIdsAffected || []),
        //   [tag.id]: val,
        // });
        const filteredSelectedValues = getFilteredSelectedValues?.(tag.id, val);
        // console.log('updated', {
        //   ...selectedTagValues,
        //   ...filteredSelectedValues,
        //   [tag.id]: val,
        // });
        onTagsValueChange({
          ...selectedTagValues,
          ...filteredSelectedValues,
          [tag.id]: val,
        });
      }
    },
    [getFilteredSelectedValues, onTagsValueChange, selectedTagValues, tag.id, tag.type],
  );

  const isRequiredTag = tag.settings.requiredTag;

  const onDelete = useCallback(() => {
    onChange(undefined);
    onDeleteTag(tag.id);
  }, [onChange, onDeleteTag, tag.id]);

  const targetValues = useMemo(() => {
    if (
      ![TagTypeEnum.MultiSelect, TagTypeEnum.SingleSelect].includes(tag.type) ||
      !isTagControlled
    ) {
      return undefined;
    }

    let targetValues: string[][] = [];
    controllingTagIds?.forEach((controllingTagId) => {
      const selectedTagValuesForControllingTag = selectedTagValues?.[
        controllingTagId
      ] as ControlTagValues;

      const targetValuesForASingleCondition: string[] = [];
      if (
        Array.isArray(selectedTagValuesForControllingTag) &&
        selectedTagValuesForControllingTag.length
      ) {
        const selectedIds = selectedTagValuesForControllingTag.map((item) => item.value);
        selectedIds.forEach((selectedId) => {
          const targetTagId = sourceToTargetMap?.[selectedId];
          const tagTargetValues = targetTagId ? targetTagId[tag.id] : [];
          // targetTagId && targetValues.push(tagTargetValues);
          if (targetTagId) {
            targetValuesForASingleCondition.push(...tagTargetValues);
          }
        });
      }

      targetValuesForASingleCondition.length && targetValues.push(targetValuesForASingleCondition);
    });

    /**
     * if !targetValues.length, it means that the controlled or controlling tag is not selected
     * in that case pass undefined to the TagValueSelectorComponent
     */
    return targetValues.length ? intersection(...targetValues) : undefined;
  }, [controllingTagIds, isTagControlled, selectedTagValues, sourceToTargetMap, tag.id, tag.type]);

  // console.log('targetValues', targetValues);

  const resolvedFilters = useMemo(() => {
    if (!targetValues) {
      return filters;
    }

    return {
      ...filters,
      valueIds: targetValues,
    };
  }, [filters, targetValues]);

  // console.log('resolvedFilters', resolvedFilters);

  // const resolvedOnChange = useCallback(
  //   (val: TagValues) => {
  //     if (
  //       ![TagTypeEnum.MultiSelect, TagTypeEnum.SingleSelect].includes(tag.type) ||
  //       !isTagControlled
  //     ) {
  //       onChange(val);
  //       return;
  //     }

  //     onChange(val);
  //   },
  //   [isTagControlled, onChange, tag.type],
  // );

  // const reslovedSelectedValues = useMemo(() => {
  //   if (
  //     ![TagTypeEnum.MultiSelect, TagTypeEnum.SingleSelect].includes(tag.type) ||
  //     !isTagControlled
  //   ) {
  //     return selectedTagValue;
  //   }

  //   const resolvedValues = (selectedTagValue as ControlTagValues)?.filter((tagValue) =>
  //     targetValues?.includes(tagValue.value),
  //   );

  //   return resolvedValues;
  // }, [isTagControlled, selectedTagValue, tag.type, targetValues]);

  // useEffect(() => {
  //   if (
  //     ![TagTypeEnum.MultiSelect, TagTypeEnum.SingleSelect].includes(tag.type) ||
  //     !isTagControlled
  //   ) {
  //     return;
  //   }

  //   const resolvedValues = (selectedTagValue as ControlTagValues)?.filter((tagValue) =>
  //     targetValues?.includes(tagValue.value),
  //   );
  //   onChange(resolvedValues);
  // }, [isTagControlled, onChange, selectedTagValue, tag.type, targetValues]);

  // useEffect(() => console.log('isTagControlled'), [isTagControlled]);
  // useEffect(() => console.log('onChange'), [onChange]);
  // useEffect(() => console.log('selectedTagValue'), [selectedTagValue]);
  // useEffect(() => console.log('tag.type'), [tag.type]);
  // useEffect(() => console.log('targetValues'), [targetValues]);
  const controllingTagNameRef = useRef<string | undefined>(undefined);

  const isControllingParentEmpty = useMemo(() => {
    if (!isTagControlled || !controllingTagIds) {
      return false;
    }
    return controllingTagIds.some((controllingTagId) => {
      const selectedValue = selectedTagValues?.[controllingTagId];
      controllingTagNameRef.current = tagsIdToNameMap?.[controllingTagId];
      return !selectedValue || (Array.isArray(selectedValue) && !selectedValue.length);
    });
  }, [controllingTagIds, isTagControlled, selectedTagValues, tagsIdToNameMap]);

  const disabledMessage = //only for single-select and multi-select tags
    isRequiredTag && isControllingParentEmpty
      ? t('Tags:Form.SetValueForCategory', {
          category: controllingTagNameRef.current || 'controlling tags',
          subCategory: tag.name,
        })
      : undefined;

  return (
    <TagValueSelectorComponent
      className={className}
      disabledMessage={disabledMessage}
      error={error}
      filters={resolvedFilters}
      readOnly={readOnly || (!!isSharedItem && !!tag.sharedByWorkspace)}
      selectedTagValue={selectedTagValue}
      tag={tag}
      onChange={onChange}
      onDelete={isRequiredTag ? undefined : onDelete}>
      {children}
    </TagValueSelectorComponent>
  );
}

export default TagValueSelector;
